/**
 * @fileoverview Language Context Provider
 * 
 * This module provides a React context for managing application language state.
 * It centralizes language selection logic and provides an easy interface for changing languages.
 * 
 * Key features:
 * - Persists language selection in localStorage
 * - Manages RTL/LTR text direction automatically
 * - Provides language switching with proper event handling
 * - Exposes the current language to all components
 * - Includes typed API for the supported languages
 * - Supports path-based localization (/en, /ar) for better SEO
 * - Maintains backward compatibility with URL query parameter (?lang=ar)
 */

import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { dynamicActivate, RTL_LOCALES, SUPPORTED_LOCALES } from '@/lib/i18n';
import { LocaleType } from '@/types/chat';
import { useHapticFeedback } from '@/hooks/use-haptic';
import i18n from '@/lib/i18n'; // Import from our configured i18n singleton

// Type definition for the language context
interface LanguageContextType {
  locale: LocaleType;
  setLocale: (locale: LocaleType) => Promise<void>;
  isRTL: boolean;
  getLanguageDisplayName: (locale: LocaleType, showNativeOnly?: boolean) => string;
  getUrlWithLanguage: (locale: LocaleType) => string;
}

// Create context with default values
const LanguageContext = createContext<LanguageContextType>({
  locale: 'en',
  setLocale: async () => {},
  isRTL: false,
  getLanguageDisplayName: () => '',
  getUrlWithLanguage: () => '',
});

// Props interface for the provider component
interface LanguageProviderProps {
  children: ReactNode;
  defaultLocale?: LocaleType;
}

/**
 * Language Provider Component
 * 
 * Provides language context to the application and handles language switching.
 * 
 * @param {LanguageProviderProps} props - Component properties
 * @returns {JSX.Element} Provider component
 */
export function LanguageProvider({ 
  children, 
  defaultLocale = 'en' 
}: LanguageProviderProps) {
  const { toast } = useToast();
  const { triggerHaptic } = useHapticFeedback();
  
  // Initialize locale from URL path, URL parameter, i18next, localStorage, or default
  const [locale, setLocaleState] = useState<LocaleType>(() => {
    try {
      const supportedLocales = Object.keys(SUPPORTED_LOCALES);
      let detectedLocale: LocaleType | null = null;
      
      // First check for language in URL path (highest priority)
      if (typeof window !== 'undefined') {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const pathLang = pathSegments[0];
        
        if (pathLang && supportedLocales.includes(pathLang)) {
          // URL path has highest priority for language detection
          detectedLocale = pathLang as LocaleType;
          
          // IMPORTANT: Force i18next to use the path language and update localStorage
          // This ensures consistency between URL, i18next and localStorage
          try {
            i18n.changeLanguage(pathLang);
            localStorage.setItem('locale', pathLang);
          } catch (e) {
            // Silently continue if localStorage or language change fails
          }
          
          return detectedLocale;
        }
      }
      
      // Then check for language parameter in URL (legacy method)
      if (typeof window !== 'undefined' && !detectedLocale) {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');
        
        if (langParam && supportedLocales.includes(langParam)) {
          // URL query parameter approach (legacy, lower priority than path)
          detectedLocale = langParam as LocaleType;
          
          // Update localStorage to maintain consistency
          try {
            localStorage.setItem('locale', langParam);
          } catch (e) {
            // Ignore storage errors
          }
          
          return detectedLocale;
        }
      }
      
      // Then use i18next's language if available
      if (!detectedLocale && i18n.language && supportedLocales.includes(i18n.language)) {
        // Use existing i18next language if available
        return i18n.language as LocaleType;
      }
      
      // Fallback to localStorage
      if (!detectedLocale) {
        const storedLocale = localStorage.getItem('locale');
        if (storedLocale && supportedLocales.includes(storedLocale)) {
          // Use localStorage language as fallback
          return storedLocale as LocaleType;
        }
      }
      
      // Default language fallback
      return defaultLocale;
    } catch (error) {
      // Silent catch - use default locale
      return defaultLocale;
    }
  });
  
  const isRTL = RTL_LOCALES.includes(locale);
  
  // Subscribe to i18next language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      if (lng && Object.keys(SUPPORTED_LOCALES).includes(lng) && lng !== locale) {
        setLocaleState(lng as LocaleType);
      }
    };
    
    // Add language change listener
    i18n.on('languageChanged', handleLanguageChanged);
    
    // Cleanup listener on unmount
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [locale]);
  
  /**
   * Change the application language using path-based navigation
   * @param {LocaleType} newLocale - The locale to switch to
   */
  const setLocale = useCallback(async (newLocale: LocaleType) => {
    try {
      // Skip if it's already the current locale
      if (newLocale === locale) return;
      
      // Provide haptic feedback for the change
      await triggerHaptic('selection');
      
      // Activate the new locale through i18next
      await dynamicActivate(newLocale);
      
      // Update locale in state (i18next will handle localStorage)
      setLocaleState(newLocale);
      
      // Update URL to use path-based language routing without reloading the page
      try {
        const url = new URL(window.location.href);
        const pathSegments = url.pathname.split('/').filter(Boolean);
        
        // Check if the first path segment is a language code
        const supportedLocales = Object.keys(SUPPORTED_LOCALES);
        const firstSegment = pathSegments[0];
        const isFirstSegmentLocale = firstSegment && supportedLocales.includes(firstSegment);
        
        // Construct new path with the language code
        let newPath: string;
        
        if (isFirstSegmentLocale) {
          // Replace the language segment
          pathSegments[0] = newLocale;
          newPath = '/' + pathSegments.join('/');
        } else {
          // Add the language as a prefix
          newPath = '/' + newLocale + (url.pathname === '/' ? '' : '/' + pathSegments.join('/'));
        }
        
        // Create a new URL with the path
        const newUrl = new URL(newPath, url.origin);
        
        // Copy all query parameters except 'lang' (we're moving to path-based)
        url.searchParams.forEach((value, key) => {
          if (key !== 'lang') {
            newUrl.searchParams.set(key, value);
          }
        });
        
        // Use History API to update URL without full page reload
        window.history.pushState({}, '', newUrl.toString());
      } catch (urlError) {
        // Silent fail - URL update is non-critical
      }
      
      // Show success toast using translated messages
      toast({
        title: SUPPORTED_LOCALES[newLocale].english,
        description: i18n.t('Language changed to {{locale}}', { locale: SUPPORTED_LOCALES[newLocale].native }),
        duration: 3000,
      });
    } catch (error) {
      // Show toast but don't log the error
      toast({
        title: i18n.t("Error"),
        description: i18n.t("Failed to change language. Please try again."),
        variant: "destructive",
      });
    }
  }, [locale, toast, triggerHaptic]);
  
  /**
   * Get display name for a language
   * @param {LocaleType} localeCode - The locale code
   * @param {boolean} showNativeOnly - Whether to show only native name
   * @returns {string} Formatted language display name
   */
  const getLanguageDisplayName = useCallback((localeCode: LocaleType, showNativeOnly = false): string => {
    const language = SUPPORTED_LOCALES[localeCode];
    return showNativeOnly
      ? language.native
      : `${language.native} (${language.english})`;
  }, []);
  
  /**
   * Get URL with a specific language path prefix while preserving other path segments and query parameters
   * @param {LocaleType} localeCode - The locale code to use in the URL
   * @returns {string} Current URL with language path prefix
   */
  const getUrlWithLanguage = useCallback((localeCode: LocaleType): string => {
    try {
      const currentUrl = new URL(window.location.href);
      const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
      
      // Check if the first path segment is already a language code
      const supportedLocales = Object.keys(SUPPORTED_LOCALES);
      const firstSegment = pathSegments[0];
      const isFirstSegmentLocale = firstSegment && supportedLocales.includes(firstSegment);
      
      // Construct new path with the requested language code
      let newPath: string;
      
      if (isFirstSegmentLocale) {
        // Replace the language segment
        pathSegments[0] = localeCode;
        newPath = '/' + pathSegments.join('/');
      } else {
        // Add the language as a prefix
        newPath = '/' + localeCode + (currentUrl.pathname === '/' ? '' : '/' + pathSegments.join('/'));
      }
      
      // Create a new URL with the path
      const newUrl = new URL(newPath, currentUrl.origin);
      
      // Copy all query parameters except 'lang' (which we're not using anymore)
      currentUrl.searchParams.forEach((value, key) => {
        if (key !== 'lang') {
          newUrl.searchParams.set(key, value);
        }
      });
      
      return newUrl.toString();
    } catch (error) {
      // Silent catch - return current URL as fallback
      return window.location.href;
    }
  }, []);
  
  // Initialize the locale on first render if needed, but only once
  useEffect(() => {
    // Only activate if i18next's current language doesn't match our state
    // and only on first mount to avoid duplicate activations
    const initialLanguage = i18n.language;
    if (initialLanguage !== locale) {
      // Use a flag in sessionStorage to prevent duplicate initial activations
      const hasActivated = sessionStorage.getItem('has_activated_locale');
      if (!hasActivated) {
        dynamicActivate(locale)
          .then(() => {
            try {
              sessionStorage.setItem('has_activated_locale', 'true');
            } catch (e) {
              // Ignore storage errors
            }
          })
          .catch(() => {
            // Silent catch - locale activation will fall back to default
          });
      }
    }
  }, []); // Only run on mount
  
  // Context value with memoized callbacks
  const contextValue: LanguageContextType = {
    locale,
    setLocale,
    isRTL,
    getLanguageDisplayName,
    getUrlWithLanguage,
  };
  
  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Custom hook to use the language context
 * @returns {LanguageContextType} Language context values and functions
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
}